/* eslint-disable prettier/prettier */
/* prettier-ignore-file */
/* eslint-disable */
import { FunctionComponent, useCallback, useEffect, useState } from 'react';
import styles from '../../styles/pages/WelcomeScreenLogin.module.css';
import IOCMImage from '../../../src/assets/images/ICM Logo.png';
import InputField from '../controlVariations/InputField';
import { Cta } from '../controlVariations/Cta';
import { useNavigate } from 'react-router-dom';
import Checkbox from '../controlVariations/checkbox';
import { useAuth } from '../../hooks/useAuth';
import CTA2 from '../controlVariations/Cta2';
import googleIcon from '../../../src/assets/images/icon/google.png';
import linkedinIcon from '../../../src/assets/images/icon/Linkdln.png';
import AuthInfoCard from '../controlVariations/AuthInfoCard';
import successIcon from '../../assets/images/success.png';
import { validateEmail } from '../../utils/validation';
import { sanitizeEmailInput } from '../../utils/sanitize';
import { useUserProfile } from '../../hooks/useUserProfile';
import { userService } from '../../services/userService';
import { jwtDecode } from 'jwt-decode';

const WelcomeScreenLogin: FunctionComponent = () => {
	const navigate = useNavigate();
	const [attempts, setAttempts] = useState(0);
	const [lockoutTime, setLockoutTime] = useState<number | null>(null);
	const [remainingTime, setRemainingTime] = useState<number>(0);
	const [loginSuccess, setLoginSuccess] = useState(false);
	const [rememberMe, setRememberMe] = useState<boolean | 'indeterminate'>(
		false
	);
	const [email, setEmail] = useState<string>('');
	const [password, setPassword] = useState<string>('');
	const [isBlocked, setIsBlocked] = useState(false);
	const [errors, setErrors] = useState<{
		email?: string;
		password?: string;
		rememberMe?: string;
	}>({});
	const apiBaseUrl = process.env.REACT_APP_BASE_API_URL || '';

	useEffect(() => {
		const token = localStorage.getItem('token');
		const user = localStorage.getItem('user');

		if (!token || !user) return;

		try {
			const decoded: any = jwtDecode(token);
			const currentTime = Date.now() / 1000;

			// 🔥 If token expired → clear and stop
			if (decoded.exp < currentTime) {
				localStorage.removeItem('token');
				localStorage.removeItem('refreshtoken');
				localStorage.removeItem('user');
				localStorage.removeItem('cachedProfile');
				return;
			}
			const parsedUser = JSON.parse(user);
			const role = parsedUser?.rolename?.toLowerCase();

			navigate('/dashboard', { replace: true });
		} catch {
			localStorage.clear();
		}
	}, [navigate]);

	useEffect(() => {
		const savedRemember = localStorage.getItem('rememberEmail');
		const savedEmail = localStorage.getItem('savedEmail');

		if (savedRemember === 'true' && savedEmail) {
			setRememberMe(true);
			setEmail(savedEmail);
		}
	}, []);

	useEffect(() => {
		const storedLockTime = localStorage.getItem('lockoutTime');
		if (storedLockTime) setLockoutTime(Number(storedLockTime));
	}, []);

	useEffect(() => {
		if (lockoutTime) {
			localStorage.setItem('lockoutTime', lockoutTime.toString());
		} else {
			localStorage.removeItem('lockoutTime');
		}
	}, [lockoutTime]);

	useEffect(() => {
		let timer: NodeJS.Timeout;

		if (lockoutTime) {
			const updateRemaining = () => {
				const timeLeft = Math.max(
					0,
					Math.floor((lockoutTime - Date.now()) / 1000)
				);
				setRemainingTime(timeLeft);

				if (timeLeft <= 0) {
					setLockoutTime(null);
					setRemainingTime(0);
					clearInterval(timer);
				}
			};

			updateRemaining(); // update immediately
			timer = setInterval(updateRemaining, 1000);
		}

		return () => clearInterval(timer);
	}, [lockoutTime]);

	const handleInputChange = (field: 'email' | 'password', value: string) => {
		// Update the state value (email or password)
		if (field === 'email') setEmail(value);
		if (field === 'password') setPassword(value);

		// Remove the specific error as user starts typing
		if (errors[field]) {
			setErrors(prevErrors => ({ ...prevErrors, [field]: undefined }));
		}
	};

	const validate = () => {
		const newErrors: {
			email?: string;
			password?: string;
			rememberMe?: string;
		} = {};

		if (!email) newErrors.email = 'Email is required';
		else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
			newErrors.email = 'Enter a valid email address';

		if (!password) newErrors.password = 'Password is required';
		else if (password.length < 8)
			newErrors.password = 'Password must be at least 8 characters';

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const validateEmailLive = (email: string): string => {
		if (!email) return '';

		// user typed text but no @ at all
		if (!email.includes('@')) {
			return 'Please enter a valid email address';
		}

		// full email validation
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		if (!emailRegex.test(email)) {
			return 'Please enter a valid email address';
		}

		return '';
	};

	const { login, forgotPassword, loading, error, handleAllSocialLogin } =
		useAuth();
	// const { login } = useAuth();

	const handleForgotPassword = async () => {
		navigate('/forgotpassword');

	};

	useEffect(() => {
		const handleKeyPress = (event: KeyboardEvent) => {
			if (event.key === 'Enter') {
				handleLogin(); // ✅ Trigger login
			}
		};

		window.addEventListener('keydown', handleKeyPress);
		return () => window.removeEventListener('keydown', handleKeyPress);
	}, [email, password]); // ✅ include dependencies

	const handleLogin = async () => {
		if (isBlocked) return;
		if (lockoutTime && Date.now() < lockoutTime) return;
		if (!validate()) return;

		try {
			const data = await login({ email, password });

			setAttempts(0);
			setLockoutTime(null);
			setLoginSuccess(true); // 👈 popup ON

			if (rememberMe) {
				localStorage.setItem('rememberEmail', 'true');
				localStorage.setItem('savedEmail', email);
			} else {
				localStorage.removeItem('rememberEmail');
				localStorage.removeItem('savedEmail');
			}
			// 🔑 Fetch profile ONCE (no hook)
			const res = await userService.getUserProfile();
			const profile = res?.data || res;
			const role = profile?.rolename?.toLowerCase();

			// ⏱️ Delay ONLY for popup visibility
			setTimeout(() => {
				navigate('/dashboard', { replace: true });
			}, 2000);
		} catch (err) {
			console.error(err);
			const newAttempts = attempts + 1;
			setAttempts(newAttempts);
			if (newAttempts >= 5) {
				setIsBlocked(true);
				setErrors({
					password: 'Your account is blocked. Please contact admin.',
				});
			} else {
				setErrors(prev => ({
					...prev,
					password: `Invalid email or password. Attempts left: ${5 - newAttempts}`,
				}));
			}
		}
	};

	const handleSocialLogin = (provider: 'google' | 'linkedin') => {
		console.log(`Initiating ${provider} login`);
		window.location.href = `${apiBaseUrl}/auth/${provider}`;
	};

	const onNotRegisteredYetClick = useCallback(() => {
		navigate('/signup');
	}, [navigate]);

	return (
		<div className={styles.welcomeScreenlogin}>
			{/* Show overlay card when login is successful */}
			{loginSuccess && (
				<div className={styles.overlay}>
					<AuthInfoCard
						title="Login Successful"
						description1="Your learning journey continues."
						description2="Pick up where you left off or explore new content."
						iconSrc={successIcon}
						iconAlt="Success"
					/>
				</div>
			)}
			<div className={styles.welcomeScreenloginChild}>
				<img className={styles.icmLogo1} alt="" src={IOCMImage} />

				<div className={styles.loginParent}>
					<div className={styles.head}>
						<div className={styles.login}>Login</div>
					</div>
					{/* Email Input */}
					<div className={styles.inputFields}>
						<div className={styles.emailId}>Email Id</div>
						<div className={styles.inputFieldParent}>
							<InputField
								type="email"
								placeholder="john.doe@gmail.com"
								value={email}
								// onChange={(e: any) =>
								//     handleInputChange('email', e.target.value)
								// }
								onChange={(e: any) => {
									const value = e.target.value;
									handleInputChange('email', value);

									const error = validateEmailLive(value);
									setErrors(prev => ({
										...prev,
										email: error,
									}));
								}}
							/>
						</div>
						{errors.email && (
							<p className={styles.errorText}>{errors.email}</p>
						)}
					</div>

					{/* Password Input */}
					<div className={styles.inputFields}>
						<div className={styles.emailId}>Password</div>
						<div className={styles.inputFieldParent}>
							<InputField
								type="password"
								placeholder="********"
								value={password}
								onChange={(e: any) =>
									handleInputChange(
										'password',
										e.target.value
									)
								}
							/>
						</div>
						{errors.password && (
							<p className={styles.errorText}>
								{errors.password}
							</p>
						)}
					</div>

					{/* Remember Me Checkbox */}
					<div className={styles.action}>
						<Checkbox
							id="rememberMe"
							label="Remember me"
							checked={rememberMe}
							onChange={setRememberMe}
							checkedColor="#00b050"
							borderColor="#ccc"
							labelColor="#333"
							labelFontSize={14}
							triState={false}
						/>
					</div>

					{lockoutTime && remainingTime > 0 && (
						<div className={styles.lockoutMessage}>
							Too many failed attempts. Please try again in{' '}
							<strong>{remainingTime}s</strong>.
						</div>
					)}
					{/* Login Button */}
					<Cta variant="primary" onClick={handleLogin}>
						Login
					</Cta>
					<CTA2
						leftIcon={googleIcon}
						title="Login with Google"
						onClick={() => {
							handleSocialLogin('google');
						}}
					/>

					<CTA2
						leftIcon={linkedinIcon}
						title="Login with LinkedIn"
						onClick={() => handleSocialLogin('linkedin')}
					/>

					<button
						type="button"
						className={styles.forgotPassword}
						onClick={handleForgotPassword}
					>
						Forgot Password?
					</button>

					<div
						className={styles.notRegisteredYetContainer}
						onClick={onNotRegisteredYetClick}
					>
						<span>
							<span>Not registered yet?</span>
							<span className={styles.span}> </span>
						</span>
						<button
							className={styles.signUpNow}
							onClick={onNotRegisteredYetClick}
						>
							Sign up now
						</button>
					</div>
					<div className={styles.privacyPolicyParent}>
						<div className={styles.privacyPolicy}>
							<button className={styles.privacyPolicybtn}>
								Privacy Policy
							</button>
						</div>
						<div className={styles.privacyPolicy}>
							<button className={styles.privacyPolicybtn}>
								Terms and Conditions
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default WelcomeScreenLogin;
